import type { MaybeRefOrGetter } from 'vue';
import type { Awaitable, MaybeGetter } from '#layers/base/types/utils';
import type { BlockRootContext, BlockSerialized, BlockSerializedChild, BlockSlotDefinition } from '#layers/block-engine/types/block';
import type { BlockEditorContext } from '#layers/block-engine/types/block-editor';
import type { FormSchema } from '#layers/block-engine/types/form-schema';
import type { FieldSelectionContext, IdFieldSelection, PrimitiveType } from '#layers/data-model';
import type { BlockBase, BlockDefinition, BlockEventDefinitionBase, BlockInjectionDefinition, BlockPropBaseDefinition, BlockPropsBaseType } from '../../../types/block';
import { computed, toValue } from 'vue';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { useReactiveBlockComponent } from '#layers/block-engine/utils/block/component';
import { isBlockSerializedChild } from '#layers/block-engine/utils/block/editor';

type SlotDefinitionOrName = string | BlockSlotDefinition;
export type BlockConfigEventDefinition = BlockEventDefinitionBase<BlockPropBaseDefinition>;

export interface BlockConfigApiBase {
	getParentProps: () => Record<string, any> | undefined;
	getRootContext: () => BlockRootContext;
}

export interface FieldBindingResolverContext<T> {
	primitiveType: PrimitiveType;
	typeOptions?: T;
}

export interface FieldBindingDefaultPropsContext<T> extends FieldBindingResolverContext<T> {
	fieldTypeId: string;
}

type FieldBindingResolver<Result, Context = FieldBindingResolverContext<Record<string, any>>> = Result | ((context: Context) => Result);

export interface BlockFieldBindingConfig<TProps extends BlockPropsBaseType = BlockPropsBaseType> {
	supportedTypes: FieldBindingResolver<string[]>;
	recommendedDisplays?: string[] | (<T extends Record<string, any> = Record<string, any>>(context: { typeOptions?: T }) => string[]);
	defaultProps?: FieldBindingResolver<Partial<TProps> | undefined, FieldBindingDefaultPropsContext<Record<string, any>>>;
}

/**
 * Declares which fields a display block needs to render its content.
 * Used by the field selection pipeline to fetch only the data a block actually uses
 * instead of fetching all fields on the related collection.
 */
export interface BlockDisplayConfig<TProps extends BlockPropsBaseType = BlockPropsBaseType> {
	/**
	 * The fields this display block requires, expressed as an `IdFieldSelection`
	 * **relative to the current collection** (the one the display block lives on).
	 *
	 * If the block needs fields from the opposite collection of a relation, it must
	 * nest them under the relation field ID:
	 *
	 * ```ts
	 * // Needs fileName from the related file collection via the block's relation:
	 * fields: (blockProps) => [{ [blockProps.fieldId]: ['field:fileName'] }]
	 *
	 * // Needs a primitive from the current collection:
	 * fields: ['field:title']
	 * ```
	 *
	 * Returning bare opposite-collection field IDs without nesting will produce
	 * incorrect API field paths (e.g. `fileName` instead of `hero.fileName`).
	 *
	 * - Static: a fixed `IdFieldSelection` array
	 * - Dynamic: a function receiving the block's static props and a `FieldSelectionContext`
	 *   for looking up collections, relation fields, and display blocks at resolution time.
	 */
	fields:
		| IdFieldSelection
		| ((props: Omit<TProps, 'value' | 'item'>, ctx: FieldSelectionContext) => IdFieldSelection);
}

export type BlockConfigHiddenFn = (payload: {
	/**
	 * Is the block being added at the root level?
	 */
	isRoot: boolean;
} & BlockConfigApiBase) => boolean;

interface BlockConfigCommonOptions<TProps extends BlockPropsBaseType> extends Partial<Omit<BlockBase, 'id' | 'hidden' | 'type'>> {
	/**
	 * Form displayed in the block settings inspector panel.
	 */
	options?: FormSchema<TProps, BlockConfigApiBase>;

	/**
	 * Editor-visible event contract for the block.
	 */
	events?: BlockConfigEventDefinition[];
	hidden?: boolean | BlockConfigHiddenFn;
}

interface BlockConfigContainerOptions<TProps extends BlockPropsBaseType> {
	slots?: MaybeGetter<SlotDefinitionOrName[], [{ props: TProps } & BlockConfigApiBase]>;
	/**
	 * Provide block injections that can be used by children blocks.
	 */
	provide?: Record<string, BlockInjectionDefinition>;
	/**
	 * Called when the block is added somewhere (for example on a page).
	 */
	onInserted?: (payload: {
		getProps: () => TProps;
		setProps: (props: TProps) => void;
	} & BlockConfigApiBase) => Awaitable<void>;
	locked?: (payload: {
		props: TProps;
	} & BlockConfigApiBase) => boolean;
	/**
	 * Override the default block drag behavior when a block is dragged over this block.
	 * This allows blocks like Grid Layout to capture drag events and handle positioning internally.
	 * When true, the block should register a drag handler using `useBlockDragHandler` in its setup.
	 */
	overrideBlockDrag?: boolean;
	/**
	 * Disables the add buttons in the builder overlay for direct children of this block.
	 * Useful for blocks like Grid Layout that handle block insertion differently.
	 */
	addButtonsDisabled?: boolean;
}

/**
 * Marks all keys from `K` as optional `never`, preventing them from being set on this config variant.
 */
type Disallow<K extends string> = { [P in K]?: never };

type DisallowedContainerKeys = 'slots' | 'provide' | 'onInserted' | 'locked' | 'overrideBlockDrag' | 'addButtonsDisabled';
type DisallowedFieldKeys = 'fieldBinding' | 'display';
type DisallowedLeafKeys = DisallowedContainerKeys | DisallowedFieldKeys;

interface BlockSimpleConfig<TProps extends BlockPropsBaseType> extends BlockConfigCommonOptions<TProps>, Disallow<DisallowedLeafKeys> {
	type?: undefined;
}

interface BlockInputConfig<TProps extends BlockPropsBaseType> extends BlockConfigCommonOptions<TProps>, Disallow<DisallowedContainerKeys | 'display'> {
	type: 'input';
	fieldBinding?: BlockFieldBindingConfig<TProps>;
}

interface BlockDisplayTypeConfig<TProps extends BlockPropsBaseType> extends BlockConfigCommonOptions<TProps>, Disallow<DisallowedContainerKeys> {
	type: 'display';
	fieldBinding?: BlockFieldBindingConfig<TProps>;
	display?: BlockDisplayConfig<TProps>;
}

interface BlockContainerConfig<TProps extends BlockPropsBaseType> extends BlockConfigCommonOptions<TProps>, BlockConfigContainerOptions<TProps>, Disallow<DisallowedFieldKeys> {
	type: 'container';
}

interface BlockListingLayoutConfig<TProps extends BlockPropsBaseType> extends BlockConfigCommonOptions<TProps>, Disallow<DisallowedFieldKeys | 'provide' | 'onInserted' | 'locked' | 'overrideBlockDrag' | 'addButtonsDisabled'> {
	type: 'listing-layout';
	slots?: MaybeGetter<SlotDefinitionOrName[], [{ props: TProps } & BlockConfigApiBase]>;
}

export type BlockConfigOnComponentOptions<TProps extends BlockPropsBaseType = BlockPropsBaseType> =
	| BlockSimpleConfig<TProps>
	| BlockInputConfig<TProps>
	| BlockDisplayTypeConfig<TProps>
	| BlockContainerConfig<TProps>
	| BlockListingLayoutConfig<TProps>;

export type AnyBlockConfigOnComponentOptions = BlockConfigOnComponentOptions<any>;

/**
 * Customize the configuration and behavior of the block.
 *
 * ⚠️ `defineBlock` is a compiler macro and should not be imported.
 */
// eslint-disable-next-line unused-imports/no-unused-vars
export function defineBlock<P extends BlockPropsBaseType>(options: BlockConfigOnComponentOptions<P>) {
	// noop
}

function normalizeBlockConfig(config: AnyBlockConfigOnComponentOptions): AnyBlockConfigOnComponentOptions {
	if (config.type === 'listing-layout' && config.hidden == null) {
		return { ...config, hidden: true } as AnyBlockConfigOnComponentOptions;
	}

	return config;
}

/**
 * @internal
 */
export function getBlockConfigFromComponent(blockComponent: any): AnyBlockConfigOnComponentOptions {
	return normalizeBlockConfig(blockComponent.__DsBlock ?? {});
}

/**
 * @internal
 */
export function getBlockConfig(block: BlockDefinition): AnyBlockConfigOnComponentOptions {
	return getBlockConfigFromComponent(block.comp);
}

/**
 * For testing purposes only.
 * @internal
 */
export function setBlockConfig(blockComponent: any, config: AnyBlockConfigOnComponentOptions) {
	blockComponent.__DsBlock = config;
}

/**
 * @internal
 */
export function useBlockConfig(block: MaybeRefOrGetter<BlockDefinition | BlockSerialized | undefined>) {
	const comp = useReactiveBlockComponent(block);
	return computed(() => comp.value ? getBlockConfigFromComponent(comp.value) : null);
}

/**
 * @internal
 */
export function useBlockConfigApi(child: MaybeRefOrGetter<BlockSerializedChild | undefined>, ctx?: Pick<BlockEditorContext, 'getParent' | 'rootContext'>): BlockConfigApiBase {
	const getChild = () => toValue(child);
	const { getParent, rootContext } = ctx ?? useBlockEditorContext();

	return {
		getParentProps: () => {
			const child = getChild();

			if (child) {
				const parent = getParent(child.id);

				if (isBlockSerializedChild(parent)) {
					return parent.props;
				}
			}
		},
		getRootContext: () => rootContext.getRootContextValues(),
	};
}
